/**
 * Audio Visualizer Component
 * Visualizes audio input with animated bars
 */

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import type { AudioVisualizerProps } from '@/types';

export function AudioVisualizer({ audioStream, isActive }: AudioVisualizerProps) {
  const animationRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode>();
  const [bars, setBars] = useState<number[]>(new Array(5).fill(0));

  useEffect(() => {
    if (!audioStream || !isActive) {
      // Reset bars when inactive
      setBars(new Array(5).fill(0));
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    // Create audio context and analyser
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(audioStream);

    analyser.fftSize = 32;
    source.connect(analyser);
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateBars = () => {
      if (analyserRef.current) {
        analyserRef.current.getByteFrequencyData(dataArray);

        // Sample 5 frequency bands
        const newBars = [
          dataArray[0] / 255,
          dataArray[2] / 255,
          dataArray[4] / 255,
          dataArray[6] / 255,
          dataArray[8] / 255,
        ];

        setBars(newBars);
      }

      animationRef.current = requestAnimationFrame(updateBars);
    };

    updateBars();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      audioContext.close();
    };
  }, [audioStream, isActive]);

  return (
    <div className="flex items-center justify-center space-x-1 h-12">
      {bars.map((height, index) => (
        <motion.div
          key={index}
          className="w-1 bg-nomade-accent rounded-full"
          animate={{
            height: isActive ? `${Math.max(height * 100, 10)}%` : '10%',
          }}
          transition={{
            duration: 0.1,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
}
